/**
 * HTTP client for the Worker auth surface — issues #115/#116/#117.
 *
 * Every call carries `credentials: 'include'` so the session cookie
 * round-trips on cross-origin dev (Vite :5173 ↔ Worker :8787) and on
 * production same-origin deploys alike. The Worker emits credentialed
 * CORS headers via `apps/worker/src/middleware/cors.ts` — the cookie
 * is the load-bearing piece of every authenticated call after sign-in.
 *
 * Errors normalise to a discriminated union so the views can render a
 * single inline-error component regardless of which call failed.
 */
import { workerOrigin } from '../worker-origin.js';

export type AuthResult<T> = { ok: true; value: T } | { ok: false; status: number; error: string; code?: string };

export interface SignInOk {
  user: { id: string; email: string };
  session: { id: string; expires_at: string; ttl_seconds: number };
  ws_token: string;
  ws_token_expires_in_seconds: number;
}

export interface SignUpOk {
  id: string;
  message: string;
}

interface ErrorBody {
  error?: string;
  code?: string;
  issues?: { path: (string | number)[]; message: string }[];
}

async function sendJson<T>(
  path: string,
  method: 'POST' | 'DELETE' | 'GET',
  body?: unknown,
): Promise<AuthResult<T>> {
  try {
    const res = await fetch(`${workerOrigin()}${path}`, {
      method,
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
    if (!res.ok) {
      let parsed: ErrorBody = {};
      try {
        parsed = (await res.json()) as ErrorBody;
      } catch {
        // Worker returned non-JSON; carry the status text instead.
      }
      const fallback = `Request failed (${res.status}).`;
      const code = parsed.code;
      const result: AuthResult<T> = { ok: false, status: res.status, error: parsed.error ?? fallback };
      if (code !== undefined) result.code = code;
      return result;
    }
    const value = (await res.json()) as T;
    return { ok: true, value };
  } catch (err) {
    return {
      ok: false,
      status: 0,
      error: err instanceof Error ? err.message : 'Network error.',
    };
  }
}

async function postJson<T>(path: string, body: unknown): Promise<AuthResult<T>> {
  try {
    const res = await fetch(`${workerOrigin()}${path}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      let parsed: ErrorBody = {};
      try {
        parsed = (await res.json()) as ErrorBody;
      } catch {
        // Worker returned non-JSON; carry the status text instead.
      }
      const fallback = `Request failed (${res.status}).`;
      const code = parsed.code;
      const result: AuthResult<T> = { ok: false, status: res.status, error: parsed.error ?? fallback };
      if (code !== undefined) result.code = code;
      return result;
    }
    const value = (await res.json()) as T;
    return { ok: true, value };
  } catch (err) {
    return {
      ok: false,
      status: 0,
      error: err instanceof Error ? err.message : 'Network error.',
    };
  }
}

export function signUp(args: { email: string; password: string; recovery_email?: string }): Promise<AuthResult<SignUpOk>> {
  return postJson<SignUpOk>('/v1/auth/sign-up', args);
}

export function signIn(args: { email: string; password: string; totp_code?: string }): Promise<AuthResult<SignInOk>> {
  return postJson<SignInOk>('/v1/auth/sign-in', args);
}

export function signOut(): Promise<AuthResult<{ ok: true }>> {
  return postJson<{ ok: true }>('/v1/auth/sign-out', {});
}

export function confirmEmail(token: string): Promise<AuthResult<{ ok: true }>> {
  return postJson<{ ok: true }>('/v1/auth/confirm-email', { token });
}

export function requestPasswordReset(email: string): Promise<AuthResult<{ ok: true }>> {
  return postJson<{ ok: true }>('/v1/auth/password-reset/request', { email });
}

export function confirmPasswordReset(args: { token: string; new_password: string }): Promise<AuthResult<{ ok: true }>> {
  return postJson<{ ok: true }>('/v1/auth/password-reset/confirm', args);
}

export function confirmTwofaDisable(token: string): Promise<AuthResult<{ ok: true }>> {
  return postJson<{ ok: true }>('/v1/auth/2fa/disable-confirm', { token });
}

// ─────────────────────────────────────────────── account settings (#152) ─

/** The signed-in user's public profile + security posture. */
export interface CurrentUser {
  id: string;
  email: string;
  recovery_email: string | null;
  created_at: string;
  email_verified_at: string | null;
  totp_enabled: boolean;
  backup_codes_remaining: number;
  is_system_admin: boolean;
  /**
   * #89 — whether the GM has acknowledged the one-time recovery / backup
   * disclaimer. `false` until acknowledged; drives the one-time modal.
   */
  recovery_disclaimer_seen: boolean;
}

/** Read the signed-in user. Backed by `GET /v1/auth/user`. */
export function fetchCurrentUser(): Promise<AuthResult<{ user: CurrentUser }>> {
  return sendJson<{ user: CurrentUser }>('/v1/auth/user', 'GET');
}

/**
 * #89 — record that the GM has acknowledged the one-time recovery /
 * backup disclaimer. Idempotent server-side; backed by
 * `POST /v1/auth/recovery-disclaimer/seen`.
 */
export function markRecoveryDisclaimerSeen(): Promise<AuthResult<{ ok: true }>> {
  return postJson<{ ok: true }>('/v1/auth/recovery-disclaimer/seen', {});
}

/**
 * #152 follow-up — set or clear the recovery email from account settings.
 * Pass `null` (or '') to clear. Backed by `POST /v1/auth/recovery-email`.
 *
 * `current_password` is MANDATORY (#F8): the recovery mailbox is where a
 * password reset is delivered, so the server re-verifies the password before
 * repointing it — a session alone is not sufficient authority. Omitting it
 * fails the route's Zod schema and comes back as
 * `400 "Invalid recovery email."`, which blames the address rather than the
 * missing credential; that is exactly how this shipped broken.
 */
export function updateRecoveryEmail(
  recovery_email: string | null,
  current_password: string,
): Promise<AuthResult<{ recovery_email: string | null }>> {
  return postJson<{ recovery_email: string | null }>('/v1/auth/recovery-email', {
    recovery_email,
    current_password,
  });
}

/** Begin TOTP enrolment — returns the secret + otpauth URI for the QR/manual entry. */
export function setupTotp(): Promise<AuthResult<{ secret_base32: string; otpauth_uri: string }>> {
  return postJson<{ secret_base32: string; otpauth_uri: string }>('/v1/auth/2fa/setup', {});
}

/** Confirm TOTP enrolment with a code — returns the one-time backup codes. */
export function confirmTotp(
  code: string,
): Promise<AuthResult<{ ok: true; backup_codes: string[]; backup_codes_message: string }>> {
  return postJson<{ ok: true; backup_codes: string[]; backup_codes_message: string }>(
    '/v1/auth/2fa/confirm',
    { code },
  );
}

/** Request 2FA disable — re-auths with the password, then emails a confirm link. */
export function requestTotpDisable(
  password: string,
): Promise<AuthResult<{ ok: true; message: string }>> {
  return postJson<{ ok: true; message: string }>('/v1/auth/2fa/disable', { password });
}

/** Mark the account for deletion (30-day grace). Backed by `DELETE /v1/auth/account`. */
export function deleteAccount(): Promise<
  AuthResult<{ id: string; pending_deletion: boolean; grace_period_days: number; message: string }>
> {
  return sendJson('/v1/auth/account', 'DELETE');
}
